import { useState, useMemo } from 'react';
import { Search } from 'lucide-react';

import { CREDENTIAL_TYPES, CREDENTIAL_CATEGORIES, getActiveCategories } from '../lib/credential-types';
import { Modal } from './Modal';
import { ServiceIcon } from './ServiceIcon';

import type { CredentialTypeDefinition } from '../lib/credential-types';

export interface CredentialTypePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (type: CredentialTypeDefinition) => void;
}

export function CredentialTypePicker({ isOpen, onClose, onSelect }: CredentialTypePickerProps) {
  const [search, setSearch] = useState('');
  const categories = getActiveCategories();

  const grouped = useMemo(() => {
    const q = search.toLowerCase();
    const filtered = q
      ? CREDENTIAL_TYPES.filter((t) => t.label.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q) || t.category.toLowerCase().includes(q))
      : CREDENTIAL_TYPES;

    const map = new Map<string, CredentialTypeDefinition[]>();
    for (const t of filtered) {
      const group = map.get(t.category) ?? [];
      group.push(t);
      map.set(t.category, group);
    }
    return map;
  }, [search]);

  const handleSelect = (type: CredentialTypeDefinition) => {
    setSearch('');
    onSelect(type);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { setSearch(''); onClose(); }}
      title="Choose Credential Type"
      actions={[{ label: 'Cancel', variant: 'secondary', onClick: () => { setSearch(''); onClose(); } }]}
    >
      <div className="flex flex-col gap-sd-3">
        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-sd-2 top-1/2 -translate-y-1/2 text-sd-text-muted" />
          <input
            type="text"
            placeholder="Search services..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-sd-3 py-sd-2 bg-sd-surface border border-sd-border/30 rounded-sd-md text-sd-sm text-sd-text placeholder:text-sd-text-muted focus:ring-2 focus:ring-sd-primary focus:outline-none transition-colors"
          />
        </div>

        {/* Grouped list */}
        <div className="max-h-[50vh] overflow-y-auto -mx-sd-4 px-sd-4">
          {categories.map((cat) => {
            const types = grouped.get(cat);
            if (!types || types.length === 0) return null;
            return (
              <div key={cat} className="mb-sd-3">
                <h3 className="text-sd-xs font-semibold text-sd-text-muted uppercase tracking-wider mb-sd-1 px-sd-1">
                  {CREDENTIAL_CATEGORIES[cat]}
                </h3>
                <div className="flex flex-col">
                  {types.map((type) => (
                    <button
                      key={type.slug}
                      className="flex items-center gap-sd-2 px-sd-2 py-sd-2 rounded-sd-md hover:bg-sd-surface-elevated transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary"
                      onClick={() => handleSelect(type)}
                    >
                      <ServiceIcon
                        slug={type.slug}
                        label={type.label}
                        brandColor={type.brandColor}
                        size={28}
                      />
                      <span className="text-sd-sm font-medium text-sd-text">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {grouped.size === 0 && (
            <p className="text-sd-sm text-sd-text-muted text-center py-sd-4">No services match your search.</p>
          )}
        </div>
      </div>
    </Modal>
  );
}
